// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.

/**
 * Content-block extraction / structuring from an uploaded file, screenshot or
 * pasted text (the v2 editor's Paste flow). Split from
 * adminBackendServiceContentGenerationMixin to keep both files ≤ 400 lines.
 */

/**
 * UTF-8-safe base64 encode — pasted rich text/HTML can trip Cloud Armor's
 * OWASP CRS XSS/SQLi rules at the load balancer when sent as raw JSON (see
 * the equivalent veraGuardrailMessage fix); base64 keeps the byte content
 * out of the WAF's text-signature matching. Backend decodes with
 * `Buffer.from(value, "base64").toString("utf8")`.
 */
function utf8ToBase64(value: string): string {
  return btoa(unescape(encodeURIComponent(value)));
}

export const adminBackendServiceContentExtractionMixin = {
  /**
   * A pasted screenshot/document uploads straight to GCS via a signed URL,
   * never through this API's own request body — same reasoning as base64 on
   * the text path above (dodges Cloud Armor), plus it keeps large binaries
   * off this Node process/Cloud Run's own compute time. Returns the gcsPath
   * the extraction endpoints below take instead of a multipart file.
   */
  prepareExtractionUpload(file: File): Promise<string> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(
        this.apiURL('contentManagement/pasteUploads/prepare'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ contentType: file.type }),
        },
      );
      if (!response.ok) {
        throw await response
          .json()
          .catch(() => new Error(`Unable to prepare upload (${response.status})`));
      }
      const json = await response.json();
      const { uploadUrl, gcsPath } = json.data ?? json;
      if (!uploadUrl || !gcsPath) {
        throw new Error(
          'Upload preparation did not return an upload URL — the backend may be out of date or unreachable.',
        );
      }
      await this.putFileViaXhr(uploadUrl, file, file.type);
      return gcsPath as string;
    });
  },

  /**
   * Extract a `TableContent`-shaped `{ rows }` from an uploaded spreadsheet,
   * document or image. Spreadsheets/CSVs are parsed structurally; images and
   * scanned PDFs are transcribed by the vision model with a response schema
   * matching the Table block. See docs/V2_EDITOR_BLOCK_PALETTE_PREVIEWS.md.
   */
  extractTableFromFile(
    modelType: string,
    modelId: string,
    file: File,
    tier?: 'fast' | 'thinking',
  ): Promise<{ rows: any[]; aiTier: 'fast' | 'thinking' | null }> {
    return this.authFetchPromise(async (token) => {
      const gcsPath = await this.prepareExtractionUpload(file);
      const response = await this.authenticatedFetch(
        this.apiURL('contentManagement/table/extract'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            gcsPath,
            mimeType: file.type,
            originalFilename: file.name,
            modelType,
            modelId,
            tier,
          }),
        },
      );
      if (!response.ok) {
        throw await response
          .json()
          .catch(() => new Error(`Table extraction failed (${response.status})`));
      }
      const json = await response.json();
      return (json.data ?? json) as { rows: any[]; aiTier: 'fast' | 'thinking' | null };
    });
  },

  /**
   * Ask AI to curate an existing Table block's rows into a mobile-friendly
   * summary: which columns matter for a narrow phone screen, condensed into
   * label/value cards. A read-only, author-reviewed override — see
   * TableContent.mobileView and blocksFromMobileView (native app).
   */
  generateTableMobileView(rows: any[]): Promise<{ mobileView: any }> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(
        this.apiURL('contentManagement/table/mobileView'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ rows }),
        },
      );
      if (!response.ok) {
        throw await response
          .json()
          .catch(() => new Error(`Mobile view generation failed (${response.status})`));
      }
      const json = await response.json();
      return (json.data ?? json) as { mobileView: any };
    });
  },

  /**
   * Transcribe an uploaded image (screenshot / photo) into a single content
   * block's `properties` for the given block `type` (`StandardHeaderText` or
   * `LeftRightList`). The vision model is constrained by a per-type response
   * schema. Tables have their own richer endpoint (`extractTableFromFile`).
   */
  extractBlockFromFile(
    modelType: string,
    modelId: string,
    type: string,
    file: File,
    tier?: 'fast' | 'thinking',
    existing?: string,
    mergeMode?: 'preserve-tidy' | 'concat-only' | 'restructure',
  ): Promise<{ properties: any; aiTier: 'fast' | 'thinking' }> {
    return this.authFetchPromise(async (token) => {
      const gcsPath = await this.prepareExtractionUpload(file);
      const response = await this.authenticatedFetch(
        this.apiURL('contentManagement/block/extract'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            gcsPath,
            mimeType: file.type,
            originalFilename: file.name,
            type,
            modelType,
            modelId,
            tier,
            existing: existing ? utf8ToBase64(existing) : undefined,
            mergeMode,
          }),
        },
      );
      if (!response.ok) {
        throw await response
          .json()
          .catch(() => new Error(`Block extraction failed (${response.status})`));
      }
      const json = await response.json();
      return (json.data ?? json) as { properties: any; aiTier: 'fast' | 'thinking' };
    });
  },

  /**
   * Re-structure a verbatim block of pasted text into one block's `properties`
   * for `type`. The backend prompt forbids any wording change — used by the
   * "Doesn't look right? Use AI" action on a paste, so the user's exact text is
   * preserved and only its structure changes.
   */
  structureBlockFromText(
    modelType: string,
    modelId: string,
    type: string,
    text: string,
    html?: string,
    tier?: 'fast' | 'thinking',
    existing?: string,
    mergeMode?: 'preserve-tidy' | 'concat-only' | 'restructure',
  ): Promise<{ properties: any; aiTier: 'fast' | 'thinking' }> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(
        this.apiURL('contentManagement/block/structure'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            type,
            text: utf8ToBase64(text),
            html: html ? utf8ToBase64(html) : undefined,
            tier,
            existing: existing ? utf8ToBase64(existing) : undefined,
            mergeMode,
            modelType,
            modelId,
          }),
        },
      );
      if (!response.ok) {
        throw await response
          .json()
          .catch(() => new Error(`Structuring failed (${response.status})`));
      }
      const json = await response.json();
      return (json.data ?? json) as { properties: any; aiTier: 'fast' | 'thinking' };
    });
  },

  /**
   * Transcribe an uploaded screenshot into one OR MORE ordered content blocks.
   * A screenshot holding a single kind of content comes back as one block (same
   * result as `extractBlockFromFile`); one that mixes prose and a table (etc.)
   * comes back as several `{ type, id, properties }` blocks for the editor to
   * insert in order. Not block-type-scoped — the model chooses each block's type.
   */
  extractBlockSectionsFromFile(
    modelType: string,
    modelId: string,
    file: File,
    tier?: 'fast' | 'thinking',
  ): Promise<{
    blocks: any[];
    aiTier: 'fast' | 'thinking';
    /** "Adjust crop" metadata for each HeroImage block, by block id. */
    cropMetaByBlockId?: Record<
      string,
      {
        source: { url: string; path: string; width: number; height: number } | null;
        boxes: { left: number; top: number; width: number; height: number }[];
        confidence: string;
      }
    >;
  }> {
    return this.authFetchPromise(async (token) => {
      const gcsPath = await this.prepareExtractionUpload(file);
      const response = await this.authenticatedFetch(
        this.apiURL('contentManagement/block/sections'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            gcsPath,
            mimeType: file.type,
            originalFilename: file.name,
            modelType,
            modelId,
            tier,
          }),
        },
      );
      if (!response.ok) {
        throw await response
          .json()
          .catch(() => new Error(`Section extraction failed (${response.status})`));
      }
      const json = await response.json();
      return (json.data ?? json) as {
        blocks: any[];
        aiTier: 'fast' | 'thinking';
        cropMetaByBlockId?: Record<
          string,
          {
            source: { url: string; path: string; width: number; height: number } | null;
            boxes: { left: number; top: number; width: number; height: number }[];
            confidence: string;
          }
        >;
      };
    });
  },
};
